/**
 * web-account-pages D1 — the website's own chat transport: `/api/webapp/chat/*` on the website
 * session (its cookie and `X-Telify-Web: 1`, through `webFetch`), never the Mini App's
 * `clientChatApi` (its global token and Telegram recovery). The event stream and the typing post
 * use `fetch` directly with the same cookie and header: the stream is a streaming body and the
 * typing post answers 204 with nothing to read. Only pure parts are shared with the Mini App: the
 * event block parser.
 *
 * A 429 (the chat route's own limiter, or the studio address's `site:page` bucket) arrives as a
 * `SiteFetchError` whose body carries `retryAfter` in seconds; `retryAfterMs` reads it.
 */
import type {
  ClientChatReadBody,
  ClientChatReadResponse,
  ClientChatSendBody,
  ClientChatSendResponse,
  ClientThreadQuery,
  ClientThreadResponse,
} from '@telify/shared';
import { parseEventBlock, type ThreadStreamEvent } from '@/lib/chat/eventBlock';

import { SiteFetchError, webFetch } from './session';

export type { ThreadStreamEvent };

const WEB_HEADERS = { 'X-Telify-Web': '1' } as const;

export function fetchWebThread(query: ClientThreadQuery): Promise<ClientThreadResponse> {
  const params = new URLSearchParams();
  if (query.room) params.set('room', query.room);
  if (query.filter) params.set('filter', query.filter);
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.afterSeq !== undefined) params.set('afterSeq', String(query.afterSeq));
  if (query.catchUpAfterSeq !== undefined) params.set('catchUpAfterSeq', String(query.catchUpAfterSeq));
  if (query.ledgerSince) params.set('ledgerSince', query.ledgerSince);
  const search = params.toString();
  return webFetch<ClientThreadResponse>(`/chat${search ? `?${search}` : ''}`);
}

/** The business's secure messages the client has not read: the header's count, read once per page load. */
export function fetchWebChatUnread(): Promise<{ secureUnread: number }> {
  return webFetch<{ secureUnread: number }>('/chat/unread');
}

export function markWebChatRead(body: ClientChatReadBody): Promise<ClientChatReadResponse> {
  return webFetch<ClientChatReadResponse>('/chat/read', { method: 'POST', body: JSON.stringify(body) });
}

/** A send with no answer in this long counts as lost; the bubble offers a retry under the same key. */
export const WEB_SEND_TIMEOUT_MS = 15_000;

/** `POST /chat/messages`: stored as a secure message, never sent through Telegram. */
export async function sendWebMessage(body: ClientChatSendBody): Promise<ClientChatSendResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WEB_SEND_TIMEOUT_MS);
  try {
    return await webFetch<ClientChatSendResponse>('/chat/messages', {
      method: 'POST',
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

/** `POST /chat/typing`: 204, nothing stored. A refusal inside the server's 3 seconds is ignored. */
export async function postWebTyping(): Promise<void> {
  try {
    await fetch('/api/webapp/chat/typing', { method: 'POST', credentials: 'same-origin', headers: WEB_HEADERS });
  } catch {
    // Typing is a courtesy: a lost post shows nothing, and the next keystroke tries again.
  }
}

/**
 * Opens the client's event stream, calls `onOpen` once the server answers 200, then `onEvent` per
 * event. Resolves when the server closes the stream; rejects on a refused or broken connection.
 */
export async function readWebChatEvents(
  signal: AbortSignal,
  onOpen: () => void,
  onEvent: (event: ThreadStreamEvent) => void,
): Promise<void> {
  const response = await fetch('/api/webapp/chat/events', {
    signal,
    credentials: 'same-origin',
    headers: { ...WEB_HEADERS, Accept: 'text/event-stream' },
  });
  if (!response.ok || !response.body) throw new SiteFetchError(response.status, undefined, 'Chat stream refused');
  onOpen();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n');
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseEventBlock(block);
      if (parsed) onEvent(parsed);
      boundary = buffer.indexOf('\n\n');
    }
  }
}

/** The wait a 429 asks for, in milliseconds; null for any other failure. */
export function retryAfterMs(error: unknown): number | null {
  if (!(error instanceof SiteFetchError) || error.status !== 429) return null;
  const seconds = Number(error.body.retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 60_000;
}
